@if (isLoading) {
  <div class="grid grid-cols-1 gap-4">
    @for (i of [1, 2]; track i) {
      <vw-skeleton-loader [width]="'100%'" [height]="'88px'" [borderRadius]="'1rem'"></vw-skeleton-loader>
    }
  </div>
} @else {
  <div class="flex flex-col gap-5">
    @for (z of zones; track z.zone.uid) {
      <section class="flex flex-col gap-3">
        <!-- En-tête de zone : identique que la zone contienne des planches/cultures ou non,
             pour qu'une zone vide reste visuellement une zone (et pas une planche). -->
        <div class="flex items-center justify-between gap-2">
          <div class="flex min-w-0 items-center gap-2 cursor-pointer" (click)="openZoneEdit(z)">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#DAF2E0] text-base">{{ zoneEmoji(z.zone.locationType) }}</span>
            <div class="min-w-0">
              <h3 class="truncate text-lg font-semibold leading-tight text-[#023E42]">{{ z.zone | vwZoneName }}</h3>
              <p class="truncate text-[11px] leading-tight text-[#9DA4A2]">
                @if (z.zone | vwHasCustomZoneName) {
                  {{ 'profile.addresses.edit-address.edit-locations.locations.' + z.zone.locationType | translate }} ·
                }
                @if (z.zone.sunchineLevels) {
                  {{ 'garden.beds.enums.exposure.' + z.zone.sunchineLevels | translate }} ·
                }
                {{ 'garden.overview.beds-count' | translate: { count: z.beds.length } }}
              </p>
            </div>
          </div>
          @if (z.locked) {
            <!-- Consultable : rien n'a été supprimé, les cultures en cours
                 restent gérables. Le mot compte — « verrouillé » ferait croire
                 à une perte. -->
            <span class="shrink-0 rounded-full bg-grey-100 px-2 py-0.5 text-[11px] text-[#6B7472]">
              🔒 {{ 'garden.overview.consultable' | translate }}
            </span>
          } @else {
          <!-- À la limite, le « + » prend la teinte premium : il n'échoue pas,
               il ouvre l'offre. Le signal est discret — un cadenas franc
               ressemblerait à une porte fermée. -->
          <button type="button" (click)="addBedToZone(z.zone)"
            class="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white text-lg leading-none active:bg-primary-50"
            [class]="
              bedLimitReached(z)
                ? 'border-[#F0C97A] text-[#B36A00]'
                : 'border-gray-200 text-[#6B7472] active:text-primary-600'
            "
            [attr.aria-label]="(bedLimitReached(z)
              ? ('premium.teaser.beds' | translate)
              : ('garden.overview.add-bed.title' | translate) + ' — ' + z.zone.name)">
            +
            @if (bedLimitReached(z)) {
              <span class="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#F79400]"></span>
            }
          </button>
          }
        </div>

        @if (z.locked) {
          <!-- Bandeau ambré : dire ce qui reste possible, pas ce qui est perdu -->
          <div class="flex items-start gap-2.5 rounded-xl border border-[#F0C97A] bg-[#FFF9EC] px-3 py-2.5">
            <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F79400] text-white">
              <vw-lock-icon class="h-3.5 w-3.5" />
            </span>
            <p class="text-xs leading-relaxed text-[#7A4B00]">
              {{ 'garden.overview.locked-explain' | translate }}
            </p>
          </div>
        }

        <!-- Planches en lignes compactes -->
        @if (z.beds.length) {
          <div class="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-lg">
            @for (b of z.beds; track b.bed.uid) {
              <div (click)="openBed(b.bed)" class="cursor-pointer active:bg-primary-50">
                <vw-bed-item [bed]="b.bed" [cultures]="b.cultures" [count]="b.count" [plantCount]="b.plantCount" [freeCells]="b.freeCells" [occupied]="b.occupied" [actionNeeded]="b.actionNeeded"></vw-bed-item>
              </div>
            }
          </div>
        }

        @if (z.locked) {
          <button type="button" (click)="openLockedStructurePaywall()"
            class="flex w-full items-center justify-center gap-2 rounded-xl border border-[#F0C97A] bg-[#FFF9EC] px-3 py-2.5 text-sm font-medium text-[#7A4B00]">
            <vw-lock-icon class="h-4 w-4" />
            {{ 'garden.overview.add-plant-here' | translate }}
          </button>
        }

        <!-- Cultures de la zone non placées dans une planche (rattachées à la zone) -->
        @if (z.looseCultures.length) {
          <div class="rounded-2xl bg-white p-3 shadow-lg">
            <p class="mb-2 text-[11px] font-medium uppercase tracking-wide text-[#9DA4A2]">🌱 {{ 'garden.overview.off-bed' | translate }}</p>
            <div class="grid grid-cols-3 gap-3">
              @for (g of z.looseCultures; track $index) {
                <div (click)="openCulture(g)" class="cursor-pointer">
                  <div class="relative aspect-square w-full overflow-hidden rounded-lg">
                    <vw-cultivation-item-viewer [cultivation]="$any(g.culture)" displayType="SMALL" [showName]="false" class="block h-full w-full"></vw-cultivation-item-viewer>
                    @if (g.actionNeeded) {
                      <vw-action-badge class="absolute left-1 top-1" [ariaLabel]="'garden.overview.action-todo' | translate"></vw-action-badge>
                    }
                    @if (g.plantCount > 1) {
                      <span class="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold leading-none text-[#023E42] shadow-sm">
                        <img src="/assets/svg/leaf.svg" alt="" class="h-3.5 w-3.5" />{{ g.plantCount }}
                      </span>
                    }
                  </div>
                  <p class="mt-1.5 truncate text-center text-sm font-semibold text-[#023E42]">{{ $any(g.culture)?.name }}</p>
                </div>
              }
            </div>
          </div>
        }
      </section>
    }

    <!-- Cultures sans zone (orphelines) uniquement — celles rattachées à une zone restent sous leur zone. -->
    @if (orphanCultures.length) {
      <section class="flex flex-col gap-2 border-t border-gray-100 pt-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-[#9DA4A2]">{{ 'garden.overview.no-zone' | translate }}</p>
        <div class="grid grid-cols-3 gap-3">
          @for (g of orphanCultures; track $index) {
            <div (click)="openCulture(g)" class="cursor-pointer">
              <div class="relative aspect-square w-full overflow-hidden rounded-lg">
                <vw-cultivation-item-viewer [cultivation]="$any(g.culture)" displayType="SMALL" [showName]="false" class="block h-full w-full"></vw-cultivation-item-viewer>
                @if (g.actionNeeded) {
                  <vw-action-badge class="absolute left-1 top-1" [ariaLabel]="'garden.overview.action-todo' | translate"></vw-action-badge>
                }
                @if (g.plantCount > 1) {
                  <span class="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold leading-none text-[#023E42] shadow-sm">
                    <img src="/assets/svg/leaf.svg" alt="" class="h-3.5 w-3.5" />{{ g.plantCount }}
                  </span>
                }
              </div>
              <p class="mt-1.5 truncate text-center text-sm font-semibold text-[#023E42]">{{ $any(g.culture)?.name }}</p>
            </div>
          }
        </div>
      </section>
    }

    @if (!zones.length && !orphanCultures.length) {
      <p class="py-8 text-center text-sm text-[#9DA4A2]">{{ 'garden.overview.empty' | translate }}</p>
    }

    <!-- En bas de l'inventaire, une fois la limite atteinte : l'utilisateur
         voit son potager complet et apprend qu'il peut l'agrandir. Avant la
         limite, l'encart n'aurait rien à débloquer. -->
    @if (structureLimitHit) {
      <div class="mt-4">
        <vw-premium-teaser
          [message]="'premium.teaser.' + structureLimitHit | translate"
          [detail]="'premium.teaser.' + structureLimitHit + '-detail' | translate"
          [source]="'garden_' + structureLimitHit"
          (ctaClicked)="openLimitPaywall()"
        ></vw-premium-teaser>
      </div>
    }
  </div>
}

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="paywallSource"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>

<!-- Panneau « Ajouter une zone » : wizard 2 étapes (la zone → ses planches), slide-in. -->
<div
  class="fixed inset-0 z-[1000] overflow-y-auto bg-white transition-transform duration-200 ease-in-out"
  [ngClass]="showZoneForm ? 'translate-x-0' : 'pointer-events-none translate-x-full'"
>
  @if (showZoneForm) {
    <vw-header-padding-safe-area [look]="'white'"></vw-header-padding-safe-area>
    <vw-zone-create-wizard (finished)="onZoneFormClosed($event)"></vw-zone-create-wizard>
  }
</div>

<!-- Édition d'une zone (tap sur l'en-tête) : formulaire existant + action « Supprimer la zone ». -->
<div
  class="fixed inset-0 z-[1000] overflow-y-auto bg-white transition-transform duration-200 ease-in-out"
  [ngClass]="showZoneEdit ? 'translate-x-0' : 'pointer-events-none translate-x-full'"
>
  @if (showZoneEdit) {
    <vw-header-padding-safe-area [look]="'white'"></vw-header-padding-safe-area>
    <div class="p-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <vw-user-adress-location-form
        [location]="editZone"
        [showDelete]="true"
        (closeLocationForm)="onZoneEditClosed($event)"
        (deleteLocation)="onZoneDeleteRequest()"
      ></vw-user-adress-location-form>
    </div>
  }
</div>

<!-- Confirmation de suppression de zone (archive ses cultures, conserve l'historique). -->
<vw-confirm-dialog
  [isOpen]="showZoneDeleteConfirm"
  [title]="'garden.overview.delete-zone.title' | translate"
  [message]="'garden.overview.delete-zone.message' | translate: { count: zoneDeleteCount }"
  [confirmLabel]="'garden.overview.delete-zone.confirm' | translate"
  [loading]="deletingZone"
  (confirmed)="confirmZoneDelete()"
  (cancelled)="cancelZoneDelete()"
></vw-confirm-dialog>
